import React, { Suspense, lazy, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Alert, Box, CircularProgress, Typography } from "@mui/material";
import SeoHead from "../components/common/SeoHead";
import { getDriveItemBytes } from "../services/drive.service";

const DocxEditor = lazy(() => import("../components/documents/DocxEditor"));

/**
 * Read-only, full-tab viewer for a file stored as a DriveItem.
 *
 * "Open in Browser" on the Files page used to hand the browser a signed Azure
 * blob URL, which always *downloads* a .docx because browsers have no OOXML
 * renderer. This page instead streams the bytes from our own origin and renders
 * them with SuperDoc — the same engine the document editor uses — so the file
 * opens in a new tab the way it does in SharePoint.
 *
 * Deliberately view-only: DriveItems have no lock or save-back path (that lives
 * on the Document flow, along with the docx write guards), so there is nothing
 * safe to save into here.
 */
const FileViewerPage: React.FC = () => {
  const { itemId } = useParams<{ itemId: string }>();
  const [searchParams] = useSearchParams();
  const name = searchParams.get("name") || "Document";

  const [bytes, setBytes] = useState<ArrayBuffer | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!itemId) return;
    let cancelled = false;
    setBytes(null);
    setError(null);
    getDriveItemBytes(itemId)
      .then((b) => {
        if (!cancelled) setBytes(b);
      })
      .catch(() => {
        if (!cancelled) {
          setError(
            "This file could not be opened. It may have been moved or deleted — " +
              "try downloading it instead.",
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [itemId]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      {/* A viewer URL carries a file id — never something to index. */}
      <SeoHead
        title={name}
        description="Read-only file preview."
        path={`/files/view/${itemId}`}
        index={false}
      />

      <Box
        sx={{
          px: 3,
          py: 1.5,
          borderBottom: "1px solid #E5E7EB",
          flexShrink: 0,
          background: "#FFFFFF",
        }}
      >
        <Typography variant="subtitle1" fontWeight={600} noWrap title={name}>
          {name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Read-only preview
        </Typography>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", background: "#F3F4F6" }}>
        {error ? (
          <Box sx={{ p: 3 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : (
          <Suspense
            fallback={
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress size={28} />
              </Box>
            }
          >
            <DocxEditor bytes={bytes} mode="viewing" />
          </Suspense>
        )}
      </Box>
    </Box>
  );
};

export default FileViewerPage;
